import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useRef } from 'react';

type Refetchable = {
  refetch: () => unknown;
};

/**
 * If a React query cache is invalidated, the corresponding useQuery hook is not automatically triggered
 * for pages that are in the background at the time.
 *
 * This hook helps to automatically execute a refetch when the page comes back to the foreground.
 *
 * The queryResult is stored in a ref so the focus effect callback stays referentially stable
 * across renders — this keeps `useFocusEffect` from resubscribing whenever the consumer passes
 * a fresh refetch identity.
 *
 * https://tanstack.com/query/latest/docs/framework/react/react-native#refresh-on-screen-focus
 */
export const useRefetchOnFocus = (queryResult: Refetchable) => {
  const firstTimeRef = useRef(true);
  const queryResultRef = useRef(queryResult);
  queryResultRef.current = queryResult;

  useFocusEffect(
    useCallback(() => {
      if (firstTimeRef.current) {
        firstTimeRef.current = false;
        return;
      }

      queryResultRef.current.refetch();
    }, [])
  );
};

// todo: this does not really work?
/*
The original suggestion from the webseite is working properly, so i replaced the old text with it
  useFocusEffect(
    useCallback(() => {
      // do not refetch when query is initially mounted
      if (isMountedRef.current) {
        refetch();
      }
    }, [refetch])
  );

  useEffect(() => {
    isMountedRef.current = true;
  }, []);
*/
